import { Suspense, lazy, useState, useEffect } from 'react'
import Footer from './components/Footer'
import Header from './components/Header'
import ScrollToTop from './components/ScrollToTop'
import ParticleMouseFollower from './components/ParticleMouseFollower'
import { Route, Routes } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Contact from './pages/contact/Contact'
import Company from './pages/compnay/Company'
import AiMotionStudio from './pages/project/aiMotionStudio'
import Dopplemate from './pages/project/Dopplmate'
import AiIntegrationLab from './pages/project/AiIntegrationLab'
import { ChevronUpIcon } from '@heroicons/react/24/solid'

// 컴포넌트들을 lazy loading으로 변경
const Landing = lazy(() => import('./pages/Landing/Landing'))
const About = lazy(() => import('./pages/About/About'))
const NotFound = lazy(() => import('./pages/notFound/NotFound'))

// Loading 컴포넌트
const LoadingFallback = () => (
	<div className="flex flex-col items-center justify-center h-screen bg-gradient-to-br from-gray-900 to-gray-800">
		{/* 메인 로더 컨테이너 */}
		<div className="relative flex flex-col items-center justify-center mb-16">
			{/* 로고 기반 그래디언트 링 */}
			<div className="relative w-24 h-24 mb-8">
				{/* 외부 회전링 */}
				<div
					className="absolute inset-0 rounded-full animate-spin"
					style={{
						background:
							'conic-gradient(from 0deg, #ec4625, #b57059, #3a6666, #00626d, #007694, #0084ad, #ec4625)',
						padding: '3px',
					}}
				>
					<div className="w-full h-full rounded-full bg-gray-900"></div>
				</div>

				{/* 내부 펄스링 */}
				<div
					className="absolute inset-3 rounded-full animate-pulse opacity-60"
					style={{
						background: 'linear-gradient(45deg, #ec4625, #0084ad)',
					}}
				></div>
			</div>

			{/* 로딩 텍스트 */}
			<div className="text-center">
				<h2
					className="text-2xl font-bold mb-3 bg-clip-text text-transparent"
					style={{
						background:
							'linear-gradient(90deg, #ec4625, #b57059, #3a6666, #00626d, #007694, #0084ad)',
						WebkitBackgroundClip: 'text',
					}}
				>
					MINDCLINE
				</h2>
				<div className="flex items-center justify-center space-x-2">
					<span className="text-gray-300 text-sm">Loading</span>
					<div className="flex space-x-1">
						<div
							className="w-2 h-2 rounded-full animate-bounce"
							style={{
								backgroundColor: '#ec4625',
								animationDelay: '0ms',
							}}
						></div>
						<div
							className="w-2 h-2 rounded-full animate-bounce"
							style={{
								backgroundColor: '#3a6666',
								animationDelay: '150ms',
							}}
						></div>
						<div
							className="w-2 h-2 rounded-full animate-bounce"
							style={{
								backgroundColor: '#0084ad',
								animationDelay: '300ms',
							}}
						></div>
					</div>
				</div>
			</div>
		</div>

		{/* 배경 파티클 효과 */}
		<div className="absolute inset-0 overflow-hidden pointer-events-none">
			{[...Array(8)].map((_, i) => (
				<div
					key={i}
					className="absolute w-3 h-3 rounded-full animate-pulse"
					style={{
						background: i % 3 === 0 ? '#ec4625' : i % 3 === 1 ? '#3a6666' : '#0084ad',
						left: `${10 + Math.random() * 80}%`,
						top: `${10 + Math.random() * 80}%`,
						opacity: 0.2,
						animationDuration: `${2 + Math.random() * 3}s`,
						animationDelay: `${Math.random() * 2}s`,
					}}
				></div>
			))}
		</div>

		{/* 하단 프로그레스 바 */}
		<div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 w-64 h-1 bg-gray-700 rounded-full overflow-hidden">
			<div
				className="h-full rounded-full animate-pulse"
				style={{
					background: 'linear-gradient(90deg, #ec4625, #0084ad)',
					width: '100%',
					animation: 'loading-bar 2s ease-in-out infinite',
				}}
			></div>
		</div>

		<style
			dangerouslySetInnerHTML={{
				__html: `
				@keyframes loading-bar {
					0% { transform: translateX(-100%); }
					50% { transform: translateX(0%); }
					100% { transform: translateX(100%); }
				}
			`,
			}}
		/>
	</div>
)

function App() {
	const { i18n } = useTranslation()
	const [showBackToTop, setShowBackToTop] = useState(false)

	// 언어 변경 시 html lang 속성 업데이트 (폰트 분기용)
	useEffect(() => {
		document.documentElement.lang = i18n.language
	}, [i18n.language])

	// 스크롤 이벤트 리스너
	useEffect(() => {
		const handleScroll = () => {
			const scrollTop = window.pageYOffset || document.documentElement.scrollTop
			setShowBackToTop(scrollTop > 500) // 500px 이상 스크롤하면 버튼 표시
		}

		window.addEventListener('scroll', handleScroll)
		return () => window.removeEventListener('scroll', handleScroll)
	}, [])

	// Back to Top 함수
	const backToTop = () => {
		window.scrollTo({ top: 0, behavior: 'smooth' })
	}

	return (
		<div className="min-h-screen bg-gray-900 text-white">
			<Suspense fallback={<LoadingFallback />}>
				{/* 마우스 팔로워 컴포넌트 */}
				<ParticleMouseFollower />

				<Header />
				<ScrollToTop />
				<Routes>
					<Route
						path="/"
						element={<Landing />}
					/>
					<Route
						path="/about"
						element={<About />}
					/>
					<Route
						path="/project/motion-studio"
						element={<AiMotionStudio />}
					/>
					<Route
						path="/project/dopplemate"
						element={<Dopplemate />}
					/>
					<Route
						path="/project/integration-lab"
						element={<AiIntegrationLab />}
					/>
					<Route
						path="/company"
						element={<Company />}
					/>
					<Route
						path="/contact"
						element={<Contact />}
					/>
					<Route
						path="*"
						element={<NotFound />}
					/>
				</Routes>
				<Footer />

				{/* 상단 이동 플로팅 버튼 */}
				{showBackToTop && (
					<button
						onClick={backToTop}
						className="fixed bottom-8 right-8 z-50 w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 
								 hover:from-blue-700 hover:to-purple-700 text-white rounded-full shadow-lg 
								 hover:shadow-xl transform hover:scale-110 transition-all duration-300 
								 flex items-center justify-center group"
						aria-label="Back to top"
					>
						<ChevronUpIcon className="w-6 h-6 group-hover:-translate-y-0.5 transition-transform duration-200" />
					</button>
				)}
			</Suspense>
		</div>
	)
}

export default App
